// Geçmiş Rapor Aktarımı — admin API wrapper'ları (Task 12).
// Route tablosu: .superpowers/sdd/2026-09-24-report-import/api-route-table.md.
// Tüm route'lar /admin/report-import altında (auth: projects.import_report); SSE URL
// notifications-store.ts (`/api/v1/notifications/stream`) pattern'iyle aynı — EventSource
// api() wrapper'ından geçmediği için tam path burada sabit olarak dışa verilir.

import type {
  ArchiveImportResponse,
  CreateImportBatchRequest,
  CreateImportBatchResponse,
  FillProjectFromReportRequest,
  ImportEstimate,
  ImportPresignRequest,
  ImportPresignResponse,
  ImportSuggestion,
  ProjectProvenanceResponse,
  ReportImport,
  ReportImportDetail,
  ReportImportsListQuery,
  ReportImportsListResponse,
} from "@mahir/shared";
import { ApiError, api, apiRaw } from "./client";

/** SSE — Jobs sekmesi canlı güncelleme. `EventSource` `api()`'den geçmez, tam path gerekir. */
export const REPORT_IMPORT_EVENTS_URL = "/api/v1/admin/report-import/events";

export async function presignImportFile(
  body: ImportPresignRequest,
): Promise<ImportPresignResponse> {
  return api<ImportPresignResponse>("/admin/report-import/files/presign", {
    method: "POST",
    body,
  });
}

/**
 * Kapak önerisi — yalnız presign'dan dönen `storagePath` ile çağrılmalı (Fix round 1
 * notu, api-route-table.md): başka bir path ile 422 `invalid_path` döner, bu kullanıcıya
 * gösterilecek bir durum değil çağrı hatasıdır.
 */
export async function suggestFromFile(storagePath: string): Promise<ImportSuggestion> {
  const res = await api<{ suggestion: ImportSuggestion }>("/admin/report-import/files/suggest", {
    method: "POST",
    body: { storagePath },
  });
  return res.suggestion;
}

export async function estimateImport(pages: number[]): Promise<ImportEstimate> {
  return api<ImportEstimate>("/admin/report-import/estimate", {
    method: "POST",
    body: { pages },
  });
}

/**
 * Parti hataları satır bazlıdır: gövde `{error, message, rowIndex, reason?}` taşır
 * (api-route-table.md). `api()`'nin `ApiError`'ı yalnız `error/message/details`
 * alanlarını yakaladığı için `rowIndex`/`reason` kaybolurdu — bu yüzden `apiRaw`
 * ile okunup `ApiError`'ın alt sınıfı olarak fırlatılır (mevcut `catch (ApiError)`
 * blokları etkilenmez).
 */
export class ImportBatchApiError extends ApiError {
  constructor(
    status: number,
    code: string,
    message: string,
    public rowIndex: number | null,
    public reason: "active_import" | "existing_project" | null,
  ) {
    super(status, code, message);
    this.name = "ImportBatchApiError";
  }
}

interface BatchErrorBody {
  error?: string;
  message?: string;
  rowIndex?: number;
  reason?: "active_import" | "existing_project";
}

export async function createImportBatch(
  body: CreateImportBatchRequest,
): Promise<CreateImportBatchResponse> {
  const res = await apiRaw<CreateImportBatchResponse & BatchErrorBody>(
    "/admin/report-import/batches",
    { method: "POST", body },
  );
  if (!res.ok) {
    throw new ImportBatchApiError(
      res.status,
      res.body.error ?? "unknown",
      res.body.message ?? `HTTP ${res.status.toString()}`,
      typeof res.body.rowIndex === "number" ? res.body.rowIndex : null,
      res.body.reason ?? null,
    );
  }
  return res.body;
}

export async function fillProjectFromReport(
  body: FillProjectFromReportRequest,
): Promise<{ import: ReportImport }> {
  return api<{ import: ReportImport }>("/admin/report-import/fill-project", {
    method: "POST",
    body,
  });
}

function buildListQuery(q: Partial<ReportImportsListQuery>): string {
  const params = new URLSearchParams();
  if (q.status) params.set("status", q.status);
  if (q.clientId) params.set("clientId", q.clientId);
  if (q.batchId) params.set("batchId", q.batchId);
  if (q.finished) params.set("finished", q.finished);
  if (q.q) params.set("q", q.q);
  if (q.page) params.set("page", String(q.page));
  if (q.pageSize) params.set("pageSize", String(q.pageSize));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export async function listImports(
  q: Partial<ReportImportsListQuery> = {},
): Promise<ReportImportsListResponse> {
  return api<ReportImportsListResponse>(`/admin/report-import/imports${buildListQuery(q)}`);
}

export async function getImport(id: string): Promise<{ import: ReportImportDetail }> {
  return api<{ import: ReportImportDetail }>(`/admin/report-import/imports/${id}`);
}

export async function retryImport(id: string): Promise<{ import: ReportImport }> {
  return api<{ import: ReportImport }>(`/admin/report-import/imports/${id}/retry`, {
    method: "POST",
  });
}

export async function cancelImport(id: string): Promise<{ import: ReportImport }> {
  return api<{ import: ReportImport }>(`/admin/report-import/imports/${id}/cancel`, {
    method: "POST",
  });
}

export async function archiveImport(id: string): Promise<ArchiveImportResponse> {
  return api<ArchiveImportResponse>(`/admin/report-import/imports/${id}/archive`, {
    method: "POST",
  });
}

export async function getProjectProvenance(projectId: string): Promise<ProjectProvenanceResponse> {
  return api<ProjectProvenanceResponse>(`/projects/${projectId}/provenance`);
}
